import type {
  LabBulkDecideInput,
  LabBulkDecideResponse,
  LabRemoteConfigResponse,
  LabSeedIdResponse,
} from '@luxuryescapes/contract-svc-lab'

import config from 'constants/config'
import request, { authOptions } from 'api/requestUtils'

export const FUNNEL_OPTIMISER_API_TIMEOUT_MS = 2500
/** How long the login path waits before giving up and continuing. */
export const SEED_ID_TIMEOUT_MS = 2500
/** SSR / default seed-id HTTP timeout. Keep this short so a stalled Lab call cannot hold HTML. */
export const SEED_ID_SERVER_TIMEOUT_MS = 1000
/** Browser login/recovery HTTP timeout. Longer than the login wait so a slow response can still persist a cookie. */
export const SEED_ID_REQUEST_TIMEOUT_MS = 15000
const DEFAULT_TIMEOUT_MS = 1000

const isLabEnabled = () => config.LAB_ENABLED

const getLabEndpointOverride = (): string | undefined => {
  const labApiHost = config.LAB_API_HOST
  if (!labApiHost) return undefined

  if (labApiHost.startsWith('http://') || labApiHost.startsWith('https://')) {
    return labApiHost
  }

  return `${config.SCHEME}://${labApiHost}`
}

/** Shared POST body for Lab surfaces that evaluate by path + attributes (header banners, modal pop-ups). */
type LabMarketingSurfaceEvaluateInput = {
  distinctId: string
  currentPath: string
  brand: App.BrandName
  attributes?: Record<string, unknown>
  platform?: 'desktop' | 'mobileWeb' | 'app'
}

type LabMarketingEvaluateArgs = {
  distinctId: string
  currentPath: string
  featureFlagContext?: App.FeatureFlagContext
  token?: string
  additionalAttributes?: Record<string, unknown>
}

type LabCampaignPreferenceInput = {
  featureFlagKey: string
  campaignId: number
}

const filterContextAttributes = (attributes?: Record<string, unknown>) => {
  if (!attributes) return undefined

  const filteredAttributes = Object.fromEntries(
    Object.entries(attributes).filter(
      ([_, value]) => value !== undefined && value !== '',
    ),
  )

  return Object.keys(filteredAttributes).length > 0
    ? filteredAttributes
    : undefined
}

const mergeAttributes = (
  baseAttributes?: Record<string, unknown>,
  additionalAttributes?: Record<string, unknown>,
) => {
  return filterContextAttributes({
    ...baseAttributes,
    ...additionalAttributes,
  })
}

const getFeatureFlagContext = (
  featureFlagContext: App.FeatureFlagContext | undefined,
) => {
  if (!featureFlagContext) return undefined

  // geoStateName / loyaltyTier are also sent via additionalAttributes (from getLabRequestAttributes in SystemActions).
  // When both are present the additionalAttributes value takes precedence in mergeAttributes,
  // which is intentional: the client-side values are the most up-to-date source.
  const geoStateName = featureFlagContext.geoStateName?.trim()

  const {
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
    deviceName,
    regionCode,
  } = featureFlagContext

  return filterContextAttributes({
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
    deviceName,
    regionCode,
    geoStateName,
  })
}

const getLabWebPlatform = (
  featureFlagContext: App.FeatureFlagContext | undefined,
): 'desktop' | 'mobileWeb' => {
  const deviceName = featureFlagContext?.deviceName?.trim().toLowerCase()
  return deviceName === 'mobile' ? 'mobileWeb' : 'desktop'
}

const buildLabMarketingEvaluateInput = ({
  distinctId,
  currentPath,
  featureFlagContext,
  additionalAttributes,
}: LabMarketingEvaluateArgs): LabMarketingSurfaceEvaluateInput => {
  const attributes = mergeAttributes(
    getFeatureFlagContext(featureFlagContext),
    additionalAttributes,
  )

  return {
    distinctId,
    currentPath,
    brand: config.BRAND,
    attributes,
    platform: getLabWebPlatform(featureFlagContext),
  }
}

const postLabMarketingEvaluate = async <TData>(
  evaluatePath: '/api/lab/header-banners' | '/api/lab/modal-popups',
  args: LabMarketingEvaluateArgs,
): Promise<TData | undefined> => {
  if (!isLabEnabled()) return undefined

  const options = args.token ? authOptions(args.token) : {}
  const endpointOverride = getLabEndpointOverride()
  const input = buildLabMarketingEvaluateInput(args)
  try {
    const response = await request.post<
      { data: TData },
      LabMarketingSurfaceEvaluateInput
    >(
      evaluatePath,
      input,
      { ...options, timeout: DEFAULT_TIMEOUT_MS },
      endpointOverride,
    )
    return response?.data
  } catch {
    return undefined
  }
}

export const getFeatureFlagDecisions = async({
  keys,
  distinctId,
  featureFlagContext,
  token,
  additionalAttributes,
  forceRefresh,
  timeout = DEFAULT_TIMEOUT_MS,
  /** When true, do not send auth cookies/bearer so Lab buckets only on distinctId. */
  omitCredentials = false,
}: {
  keys: Array<string>
  distinctId: string
  featureFlagContext?: App.FeatureFlagContext
  token?: string
  additionalAttributes?: Record<string, unknown>
  forceRefresh?: boolean
  timeout?: number
  omitCredentials?: boolean
}): Promise<Record<string, App.LabFeatureFlagResult>> => {
  if (!isLabEnabled()) return {}

  const options = omitCredentials
    ? { timeout }
    : { ...authOptions(token), timeout }
  const baseAttributes = getFeatureFlagContext(featureFlagContext)
  const endpointOverride = getLabEndpointOverride()

  const input: LabBulkDecideInput = {
    keys,
    distinctId,
    attributes: mergeAttributes(baseAttributes, additionalAttributes),
    forceRefresh,
  }
  try {
    const response = await request.post<
      LabBulkDecideResponse,
      LabBulkDecideInput
    >('/api/lab/decide', input, options, endpointOverride)
    if (!response) return {} as Record<string, App.LabFeatureFlagResult>
    return response.data ?? ({} as Record<string, App.LabFeatureFlagResult>)
  } catch {
    return {} as Record<string, App.LabFeatureFlagResult>
  }
}

export const updateLabCampaignPreference = async(
  input: LabCampaignPreferenceInput,
  accessToken: string,
): Promise<void> => {
  if (!isLabEnabled()) return

  await request.put<unknown, LabCampaignPreferenceInput>(
    '/api/lab/campaign-preference',
    input,
    authOptions(accessToken),
    getLabEndpointOverride(),
  )
}

// preview endpoint does not require authentication
export const getLabPreview = async(
  previewId: string,
): Promise<Record<string, App.LabFeatureFlagResult>> => {
  if (!isLabEnabled()) return {}

  try {
    const response = await request.get<{
      data: Record<string, App.LabFeatureFlagResult>
    }>(
      `/api/lab/preview/${previewId}`,
      { timeout: DEFAULT_TIMEOUT_MS },
      getLabEndpointOverride(),
    )
    if (!response) return {} as Record<string, App.LabFeatureFlagResult>
    return response.data
  } catch {
    return {} as Record<string, App.LabFeatureFlagResult>
  }
}

const inFlightSeedIdRequests = new Map<string, Promise<string | undefined>>()

export const getSeedIdFromApi = (
  memberId: string,
  options: { timeout?: number } = {},
): Promise<string | undefined> => {
  if (!isLabEnabled()) return Promise.resolve(undefined)

  const inFlightRequest = inFlightSeedIdRequests.get(memberId)
  if (inFlightRequest) return inFlightRequest

  const requestPromise = Promise.resolve()
    .then(() => request.get<LabSeedIdResponse>(
      `/api/lab/seed-id?memberId=${encodeURIComponent(memberId)}`,
      { timeout: options.timeout ?? SEED_ID_SERVER_TIMEOUT_MS },
      getLabEndpointOverride(),
    ))
    .then((response) => response?.data.seedId ?? undefined)
    .catch((error) => {
      console.error('Failed to fetch stitched seed ID:', error)
      return undefined
    })
    .finally(() => {
      inFlightSeedIdRequests.delete(memberId)
    })

  inFlightSeedIdRequests.set(memberId, requestPromise)
  return requestPromise
}

export const getRemoteConfigFlags = async(
  keys: Array<string>,
): Promise<Record<string, App.LabRemoteConfigResult>> => {
  if (!isLabEnabled()) return {}

  try {
    const response = await request.post<
      LabRemoteConfigResponse,
      { keys: Array<string> }
    >(
      '/api/lab/remote-config',
      { keys },
      { timeout: DEFAULT_TIMEOUT_MS },
      getLabEndpointOverride(),
    )
    if (!response) return {} as Record<string, App.LabRemoteConfigResult>
    return response.data
  } catch {
    return {} as Record<string, App.LabRemoteConfigResult>
  }
}

export const getHeaderBanners = async({
  distinctId,
  currentPath,
  featureFlagContext,
  token,
  additionalAttributes,
}: {
  distinctId: string
  currentPath: string
  featureFlagContext?: App.FeatureFlagContext
  token?: string
  additionalAttributes?: Record<string, unknown>
}): Promise<App.LabHeaderBannersResult | undefined> =>
  postLabMarketingEvaluate<App.LabHeaderBannersResult>(
    '/api/lab/header-banners',
    {
      distinctId,
      currentPath,
      featureFlagContext,
      token,
      additionalAttributes,
    },
  )

export const getModalPopups = async({
  distinctId,
  currentPath,
  featureFlagContext,
  token,
  additionalAttributes,
}: {
  distinctId: string
  currentPath: string
  featureFlagContext?: App.FeatureFlagContext
  token?: string
  additionalAttributes?: Record<string, unknown>
}): Promise<App.LabModalPopupsResult | undefined> =>
  postLabMarketingEvaluate<App.LabModalPopupsResult>('/api/lab/modal-popups', {
    distinctId,
    currentPath,
    featureFlagContext,
    token,
    additionalAttributes,
  })
